import type { NavigateFunction } from 'react-router-dom';
import type { ApiBrowserTab } from '@/types';
import { api } from '@/services/client';
import { tabService } from '@/services/tabs';
import {
  closeBrowserTabs,
  openBrowserTabs,
  pinBrowserTab,
  muteBrowserTabs,
  duplicateBrowserTab,
  reloadBrowserTab,
  switchBrowserTab,
  createBrowserBookmark,
  extractBrowserContent,
  enableFocusBlocking,
  notifyExtensionTabAction,
  requestExtensionCommandPoll,
} from '@/services/extensionBridge';
import { WS_SESSION_STORAGE_KEY } from '@/context/TabMonitorSessionContext';

export interface VoiceExecutionResult {
  success: boolean;
  message: string;
}

export interface VoiceTemplatePattern {
  id?: string;
  actionType: string;
  patterns: string[];
  description?: string;
  enabled?: boolean;
  targetUrl?: string;
  targetRoute?: string;
}

export interface ParsedVoiceTranscript {
  action: string;
  target?: string;
  confidence: number;
}

const INTENT_RULES: Array<{ keywords: string[]; action: string; confidence: number }> = [
  { keywords: ['close all', 'close the tab', 'close tab', 'close', 'shut', 'kill tab'], action: 'close_tab', confidence: 0.86 },
  { keywords: ['switch to', 'switch', 'go to', 'show me', 'open the'], action: 'switch_tab', confidence: 0.84 },
  { keywords: ['mute tab', 'mute tabs', 'mute audio', 'mute', 'silence tab', 'silence', 'unmute'], action: 'mute_tabs', confidence: 0.88 },
  { keywords: ['pin tab', 'pin the tab', 'pin', 'keep tab', 'pin this'], action: 'pin_tab', confidence: 0.87 },
  { keywords: ['stash', 'suspend', 'sleep tab', 'sleep'], action: 'stash_tab', confidence: 0.85 },
  { keywords: ['search for', 'search', 'find'], action: 'search_tabs', confidence: 0.83 },
  { keywords: ['open new tab', 'new tab', 'open tab', 'create tab'], action: 'new_tab', confidence: 0.89 },
  { keywords: ['organize tabs', 'group tabs', 'group', 'organize', 'cluster tabs'], action: 'group_tabs', confidence: 0.82 },
  { keywords: ['bookmark this tab', 'save to bookmarks', 'bookmark tab', 'bookmark', 'save tab', 'favorite'], action: 'bookmark_tab', confidence: 0.84 },
  { keywords: ['share tab', 'share', 'export tab link', 'copy tab link', 'copy link'], action: 'share_tab', confidence: 0.83 },
  { keywords: ['turn on focus mode', 'enter focus mode', 'focus mode', 'distraction free', 'focus'], action: 'focus_mode', confidence: 0.8 },
  { keywords: ['extract content', 'extract text from tab', 'extract text', 'extract article', 'read article', 'get text'], action: 'extract_content', confidence: 0.88 },
  { keywords: ['research paper', 'synthesize research', 'synthesize', 'generate paper'], action: 'synthesize_research', confidence: 0.88 },
  { keywords: ['open arxiv', 'search arxiv', 'arxiv'], action: 'open_arxiv', confidence: 0.85 },
  { keywords: ['close duplicate', 'close duplicate tabs', 'deduplicate tabs', 'dedup', 'remove duplicate urls'], action: 'dedup_tabs', confidence: 0.84 },
  { keywords: ['duplicate tab', 'clone tab', 'duplicate', 'clone current tab', 'clone'], action: 'duplicate_tab', confidence: 0.85 },
  { keywords: ['reload tab', 'refresh this tab', 'refresh tab', 'reload', 'refresh', 'hard refresh'], action: 'reload_tab', confidence: 0.86 },
  { keywords: ['export session', 'save session', 'backup tabs', 'export tabs'], action: 'export_session', confidence: 0.83 },
];

const ACTION_ALIASES: Record<string, string> = {
  close: 'close_tab',
  close_tab: 'close_tab',
  switch: 'switch_tab',
  switch_tab: 'switch_tab',
  mute: 'mute_tabs',
  mute_tabs: 'mute_tabs',
  pin: 'pin_tab',
  pin_tab: 'pin_tab',
  stash: 'stash_tab',
  stash_tab: 'stash_tab',
  sleep: 'stash_tab',
  search: 'search_tabs',
  search_tabs: 'search_tabs',
  find: 'search_tabs',
  new_tab: 'new_tab',
  open_tab: 'new_tab',
  open: 'new_tab',
  group: 'group_tabs',
  group_tabs: 'group_tabs',
  organize: 'group_tabs',
  bookmark: 'bookmark_tab',
  bookmark_tab: 'bookmark_tab',
  share: 'share_tab',
  share_tab: 'share_tab',
  focus: 'focus_mode',
  focus_mode: 'focus_mode',
  extract: 'extract_content',
  extract_content: 'extract_content',
  synthesize_research: 'synthesize_research',
  synthesize: 'synthesize_research',
  open_arxiv: 'open_arxiv',
  arxiv: 'open_arxiv',
  dedup_tabs: 'dedup_tabs',
  dedup: 'dedup_tabs',
  duplicate_tab: 'duplicate_tab',
  duplicate: 'duplicate_tab',
  reload_tab: 'reload_tab',
  reload: 'reload_tab',
  refresh: 'reload_tab',
  export_session: 'export_session',
  unknown: 'unknown',
};

const STOP_WORDS = new Set(['the', 'tab', 'tabs', 'close', 'open', 'switch', 'please', 'my', 'a', 'an']);

export function normalizeVoiceAction(action: string): string {
  const key = action.toLowerCase().replace(/:confirmed$/, '').trim();
  return ACTION_ALIASES[key] ?? key;
}

export async function resolveWorkspaceSessionId(preferred?: string | null): Promise<string | null> {
  if (preferred) return preferred;
  if (typeof window !== 'undefined') {
    const cached = sessionStorage.getItem(WS_SESSION_STORAGE_KEY);
    if (cached) return cached;
  }
  try {
    const cur = await api.get<{ session: { id: string } }>('/sessions/current');
    const id = cur.data.session?.id;
    if (id && typeof window !== 'undefined') {
      sessionStorage.setItem(WS_SESSION_STORAGE_KEY, id);
    }
    return id ?? null;
  } catch {
    return null;
  }
}

function stripWakeWords(text: string): string {
  return text
    .replace(/^(hey\s+)?khiimori[,\s]*/i, '')
    .replace(/^(please\s+|can you\s+|could you\s+)/i, '')
    .trim();
}

function extractTarget(transcript: string, action: string): string | undefined {
  const lower = transcript.toLowerCase();

  for (const prefix of ['search for ', 'find ']) {
    if (lower.includes(prefix)) {
      const idx = lower.indexOf(prefix);
      const query = transcript.slice(idx + prefix.length).trim();
      if (query) return query.replace(/\s+tab$/i, '').trim();
    }
  }

  const verbPatterns = [
    /(?:close|switch|pin|mute|stash|open|show)\s+(?:to\s+)?(?:the\s+)?(.+?)(?:\s+tab)?$/i,
    /(?:go to)\s+(?:the\s+)?(.+?)(?:\s+tab)?$/i,
  ];
  for (const pattern of verbPatterns) {
    const match = transcript.match(pattern);
    if (match?.[1]) {
      const target = match[1].replace(/^the\s+/i, '').replace(/\s+tab$/i, '').trim();
      if (target && !['tab', 'tabs', 'new', 'all'].includes(target.toLowerCase())) {
        return target;
      }
    }
  }

  if (action === 'search_tabs') {
    return transcript.trim();
  }

  return undefined;
}

function templatePatternToRegex(pattern: string): RegExp {
  const escaped = pattern
    .trim()
    .toLowerCase()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\\\{tab\\\}/g, '(.+?)')
    .replace(/\\\{query\\\}/g, '(.+?)')
    .replace(/\\\{target\\\}/g, '(.+?)')
    .replace(/\\\{url\\\}/g, '(.+?)')
    .replace(/\\\{topic\\\}/g, '(.+?)')
    .replace(/\\\{site\\\}/g, '(.+?)');
  return new RegExp(`^${escaped}$`, 'i');
}

function matchTemplates(
  transcript: string,
  templates: VoiceTemplatePattern[] = [],
): ParsedVoiceTranscript | null {
  const text = stripWakeWords(transcript);
  for (const template of templates) {
    if (template.enabled === false) continue;
    for (const pattern of template.patterns) {
      const regex = templatePatternToRegex(pattern);
      const match = text.match(regex);
      if (match) {
        return {
          action: normalizeVoiceAction(template.actionType),
          target: match[1]?.trim(),
          confidence: 0.9,
        };
      }
    }
  }
  return null;
}

export function parseVoiceTranscript(
  transcript: string,
  templates: VoiceTemplatePattern[] = [],
): ParsedVoiceTranscript {
  const text = stripWakeWords(transcript).toLowerCase();
  if (!text) {
    return { action: 'unknown', confidence: 0.5 };
  }

  const templateMatch = matchTemplates(transcript, templates);
  if (templateMatch) return templateMatch;

  for (const rule of INTENT_RULES) {
    for (const keyword of rule.keywords) {
      if (text.includes(keyword)) {
        const action = normalizeVoiceAction(rule.action);
        return {
          action,
          target: extractTarget(transcript, action),
          confidence: rule.confidence,
        };
      }
    }
  }

  return { action: 'unknown', confidence: 0.5 };
}

function tabHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

function scoreTabMatch(tab: ApiBrowserTab, needles: string[]): number {
  const title = (tab.title || '').toLowerCase();
  const url = (tab.url || '').toLowerCase();
  const host = tabHost(tab.url || '');

  let best = 0;
  for (const raw of needles) {
    const needle = raw.toLowerCase().trim();
    if (!needle || needle.length < 2) continue;
    if (title === needle) best = Math.max(best, 100);
    else if (title.includes(needle) || needle.includes(title)) best = Math.max(best, 80);
    else if (host && (host.includes(needle) || needle.includes(host))) best = Math.max(best, 70);
    else if (url.includes(needle)) best = Math.max(best, 60);
    else {
      for (const word of needle.split(/\s+/)) {
        if (word.length > 2 && (title.includes(word) || host.includes(word) || url.includes(word))) {
          best = Math.max(best, 50);
        }
      }
    }
  }
  return best;
}

function buildNeedles(target: string | undefined, transcript: string): string[] {
  const parsed = parseVoiceTranscript(transcript);
  const values = [target, parsed.target, transcript]
    .filter(Boolean)
    .map((value) => String(value).trim());

  const words = values.flatMap((value) =>
    value
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 2 && !STOP_WORDS.has(word)),
  );

  return [...new Set([...values, ...words])];
}

function activeTabs(tabs: ApiBrowserTab[]): ApiBrowserTab[] {
  return tabs.filter((tab) => !tab.status || tab.status === 'active');
}

async function findMatchingTab(
  workspaceId: string | null,
  target: string | undefined,
  transcript: string,
  action: string,
): Promise<ApiBrowserTab | undefined> {
  if (!workspaceId) return undefined;
  const { tabs } = await tabService.getTabsBySession(workspaceId, 1, 100);
  const openTabs = activeTabs(tabs);
  if (openTabs.length === 0) return undefined;

  const needles = buildNeedles(target, transcript);
  let best: ApiBrowserTab | undefined;
  let bestScore = 0;
  for (const tab of openTabs) {
    const score = scoreTabMatch(tab, needles);
    if (score > bestScore) {
      bestScore = score;
      best = tab;
    }
  }

  if (best && bestScore >= 50) return best;

  const text = stripWakeWords(transcript).toLowerCase();
  if (
    openTabs.length === 1 &&
    (action === 'close_tab' || action === 'switch_tab') &&
    (text.includes('close') || text.includes('switch') || text.includes('tab'))
  ) {
    return openTabs[0];
  }

  return undefined;
}

function notifyTabsChanged() {
  if (typeof window === 'undefined') return;
  window.postMessage({ type: 'KHIIMORI_TAB_PREVIEW_UPDATED' }, '*');
  try {
    document.dispatchEvent(new CustomEvent('khiimori-tabs-changed', { bubbles: true }));
  } catch {
    // ignore
  }
}

async function removeTabFromWorkspace(tab: ApiBrowserTab): Promise<void> {
  closeBrowserTabs({
    chromeTabIds: tab.tab_id ? [tab.tab_id] : [],
    urls: [tab.url],
    forceCloseUrls: true,
    singleUrlMatch: true,
  });
  await tabService.deleteTab(tab.id);
  requestExtensionCommandPoll();
  notifyTabsChanged();
}

function extractSearchQuery(transcript: string, target?: string): string {
  if (target?.trim()) return target.trim();
  const parsed = parseVoiceTranscript(transcript);
  if (parsed.target?.trim()) return parsed.target.trim();
  return stripWakeWords(transcript);
}

export async function executeTabVoiceAction(
  action: string,
  transcript: string,
  target: string | undefined,
  workspaceId: string | null,
  navigate: NavigateFunction,
  templates: VoiceTemplatePattern[] = [],
): Promise<VoiceExecutionResult> {
  const normalizedAction = normalizeVoiceAction(action);
  const sessionId = await resolveWorkspaceSessionId(workspaceId);

  try {
    switch (normalizedAction) {
      case 'new_tab': {
        if (!sessionId) {
          return { success: false, message: 'No workspace session — open the Live tab first or create a workspace' };
        }
        const created = await tabService.createTab(sessionId, {
          url: 'https://www.google.com',
          title: 'New Tab',
        });
        openBrowserTabs([created.url || 'https://www.google.com']);
        notifyTabsChanged();
        return { success: true, message: 'Opened a new tab in your browser' };
      }
      case 'close_tab': {
        if (!sessionId) {
          return { success: false, message: 'No workspace session — open the Live tab first' };
        }
        const text = stripWakeWords(transcript).toLowerCase();
        const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
        const open = activeTabs(tabs);

        if (text.includes('close all')) {
          if (open.length === 0) {
            return { success: false, message: 'No open tabs in this workspace' };
          }
          for (const tab of open) {
            await removeTabFromWorkspace(tab);
          }
          return { success: true, message: `Closed ${open.length} tab(s)` };
        }

        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        if (!tab) {
          return {
            success: false,
            message: open.length
              ? `Could not match a tab — try "Close the ${open[0].title || tabHost(open[0].url) || 'GitHub'} tab"`
              : 'No open tabs in this workspace',
          };
        }
        await removeTabFromWorkspace(tab);
        return { success: true, message: `Closed "${tab.title || tab.url}"` };
      }
      case 'search_tabs': {
        const query = extractSearchQuery(transcript, target);
        navigate(`/tabs?section=live&q=${encodeURIComponent(query)}`);
        return { success: true, message: `Searching for "${query}"` };
      }
      case 'group_tabs': {
        notifyExtensionTabAction('group_by_domain');
        navigate('/tabs?section=live&view=clusters');
        return { success: true, message: 'Organized open tabs into topic clusters' };
      }
      case 'stash_tab': {
        navigate('/tabs?section=live');
        return { success: true, message: 'Stashed idle tabs to reduce memory usage' };
      }
      case 'switch_tab': {
        if (!sessionId) {
          return { success: false, message: 'No workspace session — open the Live tab first' };
        }
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        if (!tab) {
          return { success: false, message: 'Could not find a matching tab to switch to' };
        }
        switchBrowserTab({ tabId: tab.tab_id, url: tab.url });
        if (tab.url) {
          openBrowserTabs([tab.url], true);
        }
        navigate('/tabs?section=live');
        return { success: true, message: `Switched to "${tab.title || tab.url}"` };
      }
      case 'pin_tab': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        pinBrowserTab({ tabId: tab?.tab_id, url: tab?.url, pinned: true });
        notifyTabsChanged();
        const tabLabel = tab?.title || tab?.url || 'current tab';
        return { success: true, message: `Pinned "${tabLabel}"` };
      }
      case 'mute_tabs': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        muteBrowserTabs({ tabId: tab?.tab_id, url: tab?.url, muted: true });
        const tabLabel = tab ? `"${tab.title || tab.url}"` : 'active tabs';
        return { success: true, message: `Muted audio on ${tabLabel}` };
      }
      case 'bookmark_tab': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        createBrowserBookmark({ url: tab?.url, title: tab?.title });
        if (tab?.id) {
          api.post('/bookmarks', { tab_id: tab.id }).catch(() => {});
        }
        const tabLabel = tab?.title || tab?.url || 'current tab';
        return { success: true, message: `Saved "${tabLabel}" to bookmarks` };
      }
      case 'duplicate_tab': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        duplicateBrowserTab({ tabId: tab?.tab_id, url: tab?.url });
        if (tab?.url) {
          openBrowserTabs([tab.url]);
          if (sessionId) {
            tabService.createTab(sessionId, {
              url: tab.url,
              title: tab.title ? `${tab.title} (Copy)` : 'New Tab',
            }).catch(() => {});
          }
          notifyTabsChanged();
        }
        const tabLabel = tab?.title || tab?.url || 'current tab';
        return { success: true, message: `Duplicated "${tabLabel}"` };
      }
      case 'reload_tab': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        reloadBrowserTab({ tabId: tab?.tab_id, url: tab?.url });
        const tabLabel = tab?.title || tab?.url || 'current tab';
        return { success: true, message: `Reloaded "${tabLabel}"` };
      }
      case 'focus_mode': {
        enableFocusBlocking(
          ['twitter.com', 'x.com', 'reddit.com', 'youtube.com', 'facebook.com', 'instagram.com', 'tiktok.com'],
          25,
        );
        navigate('/productivity');
        return { success: true, message: 'Activated 25-minute distraction-free focus mode' };
      }
      case 'share_tab': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        const urlToShare = tab?.url || (typeof window !== 'undefined' ? window.location.href : '');
        if (urlToShare && typeof navigator !== 'undefined' && navigator.clipboard) {
          await navigator.clipboard.writeText(urlToShare).catch(() => {});
        }
        const tabLabel = tab?.title || tab?.url || 'active page';
        return { success: true, message: `Copied link for "${tabLabel}" to clipboard` };
      }
      case 'extract_content': {
        const tab = await findMatchingTab(sessionId, target, transcript, normalizedAction);
        extractBrowserContent({ tabId: tab?.tab_id });
        if (tab?.url) {
          navigate(`/ai?action=summarize&url=${encodeURIComponent(tab.url)}`);
        }
        const tabLabel = tab?.title || tab?.url || 'active tab';
        return { success: true, message: `Extracted readable content from "${tabLabel}"` };
      }
      case 'synthesize_research':
        navigate('/apps/synthesize');
        return { success: true, message: 'Opened Research Synthesis studio' };
      case 'open_arxiv': {
        const query = target || 'computer science';
        const url = `https://arxiv.org/search/?query=${encodeURIComponent(query)}&searchtype=all`;
        openBrowserTabs([url]);
        return { success: true, message: `Opened arXiv search for "${query}"` };
      }
      case 'dedup_tabs': {
        if (!sessionId) {
          return { success: false, message: 'No workspace session — open the Live tab first' };
        }
        notifyExtensionTabAction('close_duplicates');
        const { tabs } = await tabService.getTabsBySession(sessionId, 1, 100);
        const seenUrls = new Set<string>();
        let closedCount = 0;
        for (const tab of activeTabs(tabs)) {
          if (!tab.url) continue;
          if (seenUrls.has(tab.url)) {
            await removeTabFromWorkspace(tab);
            closedCount++;
          } else {
            seenUrls.add(tab.url);
          }
        }
        return {
          success: true,
          message: closedCount > 0 ? `Closed ${closedCount} duplicate tab(s)` : 'No duplicate tabs found',
        };
      }
      case 'export_session':
        navigate('/settings?section=sessions');
        return { success: true, message: 'Opened Session Manager to export session' };
      case 'unknown':
        return {
          success: false,
          message: 'Could not map that phrase — try "Close the GitHub tab", "Search for React", or "Open new tab"',
        };
      default: {
        // 1. Check if user-configured custom template matches
        const matchedTemplate = templates.find(
          (t) => normalizeVoiceAction(t.actionType) === normalizedAction,
        );

        const targetValue = (target || extractSearchQuery(transcript)).trim();

        if (matchedTemplate?.targetUrl) {
          let resolved = matchedTemplate.targetUrl;
          if (resolved.includes('{query}') || resolved.includes('{tab}') || resolved.includes('{target}') || resolved.includes('{topic}') || resolved.includes('{url}')) {
            resolved = resolved.replace(/\{(?:query|tab|target|topic|url)\}/gi, encodeURIComponent(targetValue));
          }
          openBrowserTabs([resolved]);
          return { success: true, message: `Opened "${resolved}"` };
        }

        if (matchedTemplate?.targetRoute) {
          navigate(matchedTemplate.targetRoute);
          return { success: true, message: `Navigated to ${matchedTemplate.targetRoute}` };
        }

        // 2. Common website mappings for open_* actions
        const COMMON_SITES: Record<string, string> = {
          youtube: 'https://www.youtube.com',
          github: 'https://github.com',
          gmail: 'https://mail.google.com',
          mail: 'https://mail.google.com',
          chatgpt: 'https://chat.openai.com',
          reddit: 'https://www.reddit.com',
          twitter: 'https://x.com',
          x: 'https://x.com',
          spotify: 'https://open.spotify.com',
          notion: 'https://www.notion.so',
          docs: 'https://docs.google.com',
          calendar: 'https://calendar.google.com',
          maps: 'https://maps.google.com',
          linkedin: 'https://www.linkedin.com',
          netflix: 'https://www.netflix.com',
          amazon: 'https://www.amazon.com',
          stackoverflow: 'https://stackoverflow.com',
          linear: 'https://linear.app',
          figma: 'https://www.figma.com',
          hackernews: 'https://news.ycombinator.com',
        };

        if (normalizedAction.startsWith('open_')) {
          const siteKey = normalizedAction.replace(/^open_/, '').toLowerCase();
          if (COMMON_SITES[siteKey]) {
            let url = COMMON_SITES[siteKey];
            if (siteKey === 'youtube' && targetValue) {
              url = `https://www.youtube.com/results?search_query=${encodeURIComponent(targetValue)}`;
            } else if (siteKey === 'github' && targetValue) {
              url = `https://github.com/search?q=${encodeURIComponent(targetValue)}`;
            }
            openBrowserTabs([url]);
            const siteName = siteKey.charAt(0).toUpperCase() + siteKey.slice(1);
            return { success: true, message: `Opened ${siteName}` };
          }
          if (targetValue && (targetValue.includes('.') || targetValue.startsWith('http'))) {
            const url = targetValue.startsWith('http') ? targetValue : `https://${targetValue}`;
            openBrowserTabs([url]);
            return { success: true, message: `Opened ${url}` };
          }
          const guessedUrl = `https://${siteKey}.com`;
          openBrowserTabs([guessedUrl]);
          return { success: true, message: `Opened ${guessedUrl}` };
        }

        // 3. Web search actions
        if (normalizedAction.startsWith('search_')) {
          const engine = normalizedAction.replace(/^search_/, '').toLowerCase();
          let searchUrl = `https://www.google.com/search?q=${encodeURIComponent(targetValue)}`;
          if (engine === 'youtube') {
            searchUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(targetValue)}`;
          } else if (engine === 'reddit') {
            searchUrl = `https://www.reddit.com/search/?q=${encodeURIComponent(targetValue)}`;
          } else if (engine === 'github') {
            searchUrl = `https://github.com/search?q=${encodeURIComponent(targetValue)}`;
          } else if (engine === 'wikipedia') {
            searchUrl = `https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(targetValue)}`;
          }
          openBrowserTabs([searchUrl]);
          return { success: true, message: `Searched ${engine} for "${targetValue}"` };
        }

        // 4. Navigation actions (nav_* or go_*)
        if (normalizedAction.startsWith('nav_') || normalizedAction.startsWith('go_')) {
          const destination = normalizedAction.replace(/^(nav_|go_)/, '').toLowerCase();
          const routeMap: Record<string, string> = {
            home: '/home',
            tabs: '/tabs?section=live',
            tab: '/tabs?section=live',
            live: '/tabs?section=live',
            ai: '/ai',
            studio: '/ai',
            productivity: '/productivity',
            focus: '/productivity',
            insights: '/insights',
            analytics: '/insights',
            apps: '/apps',
            settings: '/settings',
            history: '/tabs?section=history',
            recycle: '/tabs?section=recycle',
            trash: '/tabs?section=recycle',
            memory: '/tabs?section=memory',
            time_machine: '/tabs?section=time-machine',
          };
          const targetRoute = routeMap[destination] || `/${destination}`;
          navigate(targetRoute);
          return { success: true, message: `Navigated to ${destination}` };
        }

        // 5. AI Actions
        if (normalizedAction === 'ask_ai' || normalizedAction === 'ai_ask' || normalizedAction === 'summarize') {
          navigate(`/ai?prompt=${encodeURIComponent(targetValue || transcript)}`);
          return { success: true, message: `Opened AI Studio with prompt: "${targetValue || transcript}"` };
        }

        // 6. Direct URL or search fallback
        if (targetValue && (targetValue.includes('.') || targetValue.startsWith('http'))) {
          const url = targetValue.startsWith('http') ? targetValue : `https://${targetValue}`;
          openBrowserTabs([url]);
          return { success: true, message: `Opened ${url}` };
        }

        if (targetValue) {
          navigate(`/tabs?section=live&q=${encodeURIComponent(targetValue)}`);
          return { success: true, message: `Searching tabs for "${targetValue}"` };
        }

        return { success: false, message: `Command "${normalizedAction}" executed` };
      }
    }
  } catch (err) {
    return { success: false, message: err instanceof Error ? err.message : 'Command failed' };
  }
}

function speakFeedback(message: string) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(message);
  utterance.rate = 1;
  window.speechSynthesis.speak(utterance);
}

export function playVoiceFeedback(message: string, enabled: boolean) {
  if (enabled) speakFeedback(message);
}
